<template>
    <div class="navbarHeader">
        <div class="navbarHeader_business">
            <div class="navbarHeader_business_minimizer" :class="{active: isMinimize}" @click="changeMinimize(!isMinimize)">
                <sw-icon name="list2"/>
            </div>

            <!-- Botão mobile toggle -->
            <div class="navbarHeader_business_mobileToggle" @click="toggleMobileMenu">
                <sw-icon name="menu" />
            </div>

            <div class="navbarHeader_business_logo">
                <img src="../../assets/images/logo/logo.png" alt="Filesave">
                <!-- <p class="navbarHeader_business_logo_title">Criação de Logotipo</p> -->
            </div>
        </div>

        <div class="navbarHeader_actions">
            <div class="navbarHeader_actions_left">
                <div class="navbarHeader_actions_NewDocument">
                    <sw-button
                        name="Adicionar arquivo"
                        icon="upload"
                        color="blue"
                        size="sm"
                        @click="openUploadModal"
                    />
                </div>
            </div>

            <div>
                <div class="navbarHeader_actions_icons">
                    <div>
                        <div @click.stop="openOrCloseNotification()" data-notification-trigger>
                            <md-badge class="md-primary" md-content="1" md-position="top">
                                <sw-icon name="bell" />   
                            </md-badge>
                        </div>
                        
                        <transition name="notificationFade">
                            <Notification v-show="isOpenNotification" />
                        </transition>
                        
                    </div>
                </div>

                <div class="navbarHeader_actions_profile" @click="changeProfile(true)">
                    <img :src="auth.image" :alt="auth.name" :title="auth.name">

                    <div class="navbarHeader_actions_profile_info">
                        <p class="navbarHeader_actions_profile_name">{{ auth.name }}</p>
                        <UserPlanBadge
                            v-if="currentUser?.plan"
                            :plan="currentUser.plan"
                            :variant="PLAN_BADGE_VARIANTS.NAVBAR"
                            :show-label="false"
                        />
                    </div>
                    
                </div>
            </div>
        </div>

        <!-- Modal de Upload de Documento -->
        <sw-modal
            name="uploadModal"
            :visible="showUploadModal"
            :closeOverlayClick="false"
            @update="closeUploadModal"
        >
            <template #header>
                <ModalHeader
                    icon="upload"
                    title="Adicionar Arquivo"
                    subtitle="Faça o upload de um novo arquivo"
                    color="blue"
                />
            </template>
            
            <template #content>
                <div class="uploadModalContent">
                    <UploadFileForm
                        @cancel="closeUploadModal"
                        @upload-success="onUploadSuccess"
                    />
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Compartilhamento -->
        <sw-modal
            name="sharingModal"
            :visible="showSharingModal"
            @update="closeSharingModal"
        >
            <template #header>
                <ModalHeader
                    icon="share"
                    title="Criar Página de Compartilhamento"
                    subtitle="Crie uma página personalizada para compartilhar seus arquivos"
                    color="green"
                />
            </template>
            
            <template #content>
                <div class="uploadModalContent">
                    <p>Funcionalidade em desenvolvimento...</p>
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Upload de Terceiros -->
        <sw-modal
            name="uploadThirdPartyModal"
            :visible="showUploadThirdPartyModal"
            @update="closeUploadThirdPartyModal"
        >
            <template #header>
                <ModalHeader
                    icon="upload"
                    title="Criar Área de Upload"
                    subtitle="Crie uma área para que outras pessoas possam enviar arquivos"
                    color="purple"
                />
            </template>
            
            <template #content>
                <div class="uploadModalContent">
                    <p>Funcionalidade em desenvolvimento...</p>
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Adicionar Envio -->
        <sw-modal
            name="sendModal"
            :visible="showSendModal"
            @update="closeSendModal"
        >
            <template #header>
                <ModalHeader
                    icon="send"
                    title="Criar Envio Automático"
                    subtitle="Configure envios automáticos de arquivos por email"
                    color="blue"
                />
            </template>

            <template #content>
                <div class="uploadModalContent">
                    <p>Funcionalidade em desenvolvimento...</p>
                </div>
            </template>
        </sw-modal>

        <!-- Modal de Adicionar Alerta -->
        <sw-modal
            name="alertModal"
            :visible="showAlertModal"
            @update="closeAlertModal"
        >
            <template #header>
                <ModalHeader
                    icon="bell"
                    title="Criar Alerta Inteligente"
                    subtitle="Configure alertas inteligentes para vencimento de arquivos"
                    color="orange"
                />
            </template>
            
            <template #content>
                <div class="uploadModalContent">
                    <p>Funcionalidade em desenvolvimento...</p>
                </div>
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted } from 'vue';
    import { useStore } from 'vuex';

    import { notify } from 'sysweb-vue-components/src/global';
    import { fetchCurrentUser } from '@/services/mockApi';
    import { PLAN_BADGE_VARIANTS } from '@/constants';

    import UploadFileForm from '@/components/forms/UploadFileForm.vue';
    import Notification from '@Component/container/NotificationComponent';
    import UserPlanBadge from '@Component/UserPlanBadge';
    import ModalHeader from '@Component/modals/ModalHeader';

    const store = useStore();

    const isOpenNotification = ref(false);
    const currentUser = ref(null);
    const showUploadModal = ref(false);

    const auth = computed(() => store.state.auth);
    const isMinimize = computed(() => store.state.isMinimize);

    // Funções para gerenciar cookie
    const setCookie = (name, value, days = 30) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const loadCurrentUser = async () => {
        try {
            const response = await fetchCurrentUser();
            if (response.status === 'success') {
                currentUser.value = response.data;
            }
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
        }
    };

    onMounted(() => {
        document.body.addEventListener('click', handleClickOutside);
        loadCurrentUser();

        // Carregar estado do menu do cookie
        const savedMenuState = getCookie('menuMinimized');
        if (savedMenuState !== null) {
            const isMenuMinimized = savedMenuState === 'true';
            store.commit('changeMinimize', isMenuMinimized);
        }
    });

    onUnmounted(() => {
        document.body.removeEventListener('click', handleClickOutside);
    });

    const changeMinimize = (payload) => {
        store.commit('changeMinimize', payload);
        // Salvar estado no cookie
        setCookie('menuMinimized', payload.toString());
    };

    const changeProfile = (payload) => {
        store.commit('changeProfile', payload);
    };

    const toggleMobileMenu = () => {
        store.commit('toggleMobileMenu');
    };

    const openOrCloseNotification = () => {
        isOpenNotification.value = !isOpenNotification.value;
    };

    const handleClickOutside = (event) => {
        const notificationElement = event.target.closest('.notification');
        const triggerElement = event.target.closest('[data-notification-trigger]');

        if (!notificationElement && !triggerElement && isOpenNotification.value) {
            isOpenNotification.value = false;
        }

    };

    // Funções do modal de upload
    const openUploadModal = () => {
        showUploadModal.value = true;
    };

    const closeUploadModal = () => {
        showUploadModal.value = false;
    };

    const onUploadSuccess = ({ name, resetForm }) => {
        notify(`O arquivo "${name}" foi salvo com sucesso.`, 'success');
        closeUploadModal();
    };

    // Expor funções para componentes pais
    defineExpose({
        openUploadModal
    });
</script>

<style lang="scss">
    .navbarHeader {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .navbarHeader_business {
        position: relative;
        min-width: 310px;
    }

    .navbarHeader_business_minimizer {
        position: absolute;
        top: 50%;
        right: -22px;
        background: var(--backgroundCard);
        border: 2px solid var(--borderColorLight);
        border-radius: 50%;
        padding: 0.75em;
        font-size: 1.125em;
        color: var(--textMuted);
        cursor: pointer;
        transform: translateY(-50%);
        transition: var(--transitionDefault);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3em;
        height: 3em;
        box-shadow: var(--boxShadowSm);
        z-index: 10;

        &:hover {
            border-color: var(--linkColor);
            color: var(--linkColor);
            transform: translateY(-50%) scale(1.1);
            box-shadow: var(--boxShadowMedium);
            background: var(--backgroundHover);
        }

        &.active {
            border-color: var(--linkColor);
            color: var(--textWhite);
            background: linear-gradient(135deg, var(--linkColor), var(--colorBlueDark100));
            transform: translateY(-50%) rotate(180deg);
            box-shadow: var(--boxShadowLarge);

            &:hover {
                transform: translateY(-50%) rotate(180deg) scale(1.1);
                background: linear-gradient(135deg, var(--colorBlueDark100), var(--linkColor));
            }
        }

        & .sw-icon{
            transition: var(--transitionDefault);
        }
    }

    .navbarHeader_business_logo{
        padding: 30px;
        border-bottom: 1px solid var(--dashboardBorderColor);
        border-right: 1px solid var(--dashboardBorderColor);
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: var(--dashboardHeaderHeight);
        max-height: var(--dashboardHeaderHeight);

        & img{
            width: 175px;
        }
    }

    /* Botão Mobile Toggle */
    .navbarHeader_business_mobileToggle {
        display: none;
        position: absolute;
        top: 50%;
        left: 20px;
        transform: translateY(-50%);
        background: var(--backgroundCard);
        border: 2px solid var(--borderColorLight);
        border-radius: 8px;
        padding: 12px;
        font-size: 1.2em;
        color: var(--textMuted);
        cursor: pointer;
        transition: var(--transitionDefault);
        z-index: 15;
        box-shadow: var(--boxShadowSm);

        &:hover {
            border-color: var(--linkColor);
            color: var(--linkColor);
            background: var(--backgroundHover);
            box-shadow: var(--boxShadowMedium);
        }

        .sw-icon {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .navbarHeader_actions{
        width: 100%;
        height: var(--dashboardHeaderHeight);
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid var(--dashboardBorderColor);
        padding: 29px;
        padding-left: 45px;

        & > div {
            display: flex;
        }
    }

    .navbarHeader_actions_left {
        display: flex;
        align-items: center;
        gap: 2em;
    }

    .navbarHeader_actions_NewDocument {
        display: flex;
        align-items: center;
    }

    .navbarHeader_actions_icons{
        display: flex;
        align-items: center;
        font-size: 20px;
        margin-right: 50px;
        transition: all 0.1s ease;

        & > * {
            position: relative;
            margin-left: 26px;
            cursor: pointer;
        }

        & .icon:hover{
            color: var(--linkColor);
        }
        
        & .md-badge-content{
            padding: 10px;
        }
    }

    .navbarHeader_actions_profile{
        display: flex;
        align-items: center;
        cursor: pointer;
        text-align: center;

        img {
            width: 45px;
            border-radius: 50%;
            margin-right: 10px;
        }
    }

    .navbarHeader_actions_profile_info {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25em;
        text-align: center;
    }

    .navbarHeader_actions_profile_name {
        margin: 0;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary);
        line-height: 1.2;
    }


    /* =======================
       MODAL DE UPLOAD STYLES
       ======================= */
    .uploadModalHeader {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 0.5em 0;
    }

    .uploadModalHeader_icon {
        .sw-icon {
            width: 2.5em;
            height: 2.5em;
            background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
            color: white;
            border-radius: 0.5em;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.25em;
        }
    }

    /* =======================
       FORMULÁRIO DE UPLOAD
       ======================= */

    .uploadForm_field {
        display: flex;
        flex-direction: column;
        gap: 0.5em;
    }


    .uploadForm_fieldGroup {
        display: flex;
        flex-direction: column;
        gap: 1em;
        padding: 1.25em;
        background: rgba(248, 250, 252, 0.6);
        border: 1px solid rgba(229, 231, 235, 0.8);
        border-radius: 0.75em;
    }

    .uploadForm_fieldHeader {
        display: flex;
        align-items: center;
        gap: 0.75em;
    }


    .uploadForm_subFields {
        display: flex;
        flex-direction: column;
        gap: 1em;
        padding-left: 2em;
        border-left: 3px solid var(--colorBlue, #3b82f6);
        margin-left: 0.75em;
    }

    .uploadForm_inputGroup {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }

    .uploadForm_inputSuffix {
        font-size: 0.875em;
        color: var(--textSecondary, #6b7280);
        font-weight: 500;
    }


    .uploadModalActions {
        display: flex;
        gap: 1em;
        justify-content: flex-end;
        padding-top: 1.5em;
        border-top: 1px solid var(--borderColorLight, #e5e7eb);
        margin-top: 1.5em;
    }

    /* Responsividade */
    @media (max-width: 768px) {
        .uploadModalContent {
            padding: 1em;
        }
        
        .uploadForm {
            gap: 1.25em;
        }
        
        .uploadForm_fieldGroup {
            padding: 1em;
        }
        
        .uploadForm_subFields {
            padding-left: 1.5em;
        }
        
        .uploadModalActions {
            flex-direction: column;
            gap: 0.75em;
        }
    }

    /* ===============================
       RESPONSIVE DESIGN - MOBILE NAVBAR
       =============================== */
    @media (max-width: 768px) {
        .navbarHeader_business {
            min-width: auto;
        }


        /* Mostrar botão mobile toggle */
        .navbarHeader_business_mobileToggle {
            display: flex !important;
        }

        /* Esconder botão desktop minimizer e logo */
        .navbarHeader_business_minimizer,
        .navbarHeader_business_logo {
            display: none;
        }

        /* Mostrar actions (sino e perfil) */
        .navbarHeader_actions {
            display: flex !important;
            justify-content: flex-end;
            padding: 15px 20px;
            height: 65px;
        }

        /* Esconder todos os botões de ação rápida em mobile */
        .navbarHeader_actions_left {
            display: none !important;
        }

        /* Ajustar espaçamento das actions mobile */
        .navbarHeader_actions_icons {
            margin-right: 20px;
            font-size: 18px;
        }

        .navbarHeader_actions_profile {
            img {
                width: 35px;
                height: 35px;
            }
        }

        .navbarHeader_actions_profile_name {
            font-size: 0.85em;
        }
    }

</style>